import { Save, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyState from "../../components/EmptyState.jsx";
import { Field } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import RoutineBoard, { RoutineBoardLegend } from "../../components/RoutineBoard.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { saveRoutineLog } from "../../firebase/firestore.js";
import { getNowTimeValue } from "../../utils/date.js";
import { matchesSearch, searchableRoutine } from "../../utils/search.js";
import { timeRangeLabel } from "../../utils/time.js";

function roomCanBeEntered(roomNumber) {
  const value = String(roomNumber || "").trim().toUpperCase();
  return !value || value === "N/A";
}

export default function AddByRoutine() {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();
  const { date, day, routines, logs, settings, loading, routineMasters } = useOutletContext();

  const logByRoutine = useMemo(() => Object.fromEntries(logs.map((log) => [log.routineId, log])), [logs]);
  const visibleRoutines = useMemo(() => {
    return routines.filter((routine) => {
      const log = logByRoutine[routine.id] || logByRoutine[routine.routineId];
      return matchesSearch(searchableRoutine(routine, log, ""), search, [
        "roomNumber",
        "teacherName",
        "className",
        "sectionName",
        "subjectName",
        "scheduledIn",
        "scheduledOut",
        "day",
        "actualIn",
        "actualOut"
      ]);
    });
  }, [routines, logByRoutine, search]);

  function openModal(routine, log) {
    setModal({
      routine,
      log,
      actualIn: log?.actualIn || getNowTimeValue(),
      actualOut: log?.actualOut || "",
      addOut: Boolean(log?.actualOut),
      teacherName: log?.teacherName || routine.teacherName || "",
      roomNumber: roomCanBeEntered(log?.roomNumber || routine.roomNumber) ? "" : log?.roomNumber || routine.roomNumber || ""
    });
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const roomPatch = roomCanBeEntered(modal.routine?.roomNumber) && modal.roomNumber?.trim()
        ? { roomNumber: modal.roomNumber.trim() }
        : {};
      await saveRoutineLog({
        routine: modal.routine,
        date,
        day,
        patch: {
          actualIn: modal.actualIn,
          ...(modal.addOut ? { actualOut: modal.actualOut || getNowTimeValue() } : {}),
          ...(modal.teacherName ? { teacherName: modal.teacherName } : {}),
          ...roomPatch,
          classStatus: "normal",
          suspensionReason: ""
        },
        user,
        settings
      });
      showToast("Time saved");
      setModal(null);
    } catch (error) {
      showToast(error.message || "Unable to save time", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="page-hero compact">
        <p>{day}, {date}</p>
        <h2>Add by Routine</h2>
      </section>
      <SearchBar value={search} onChange={setSearch} placeholder="Search room, class, subject, teacher or time" />
      <RoutineBoardLegend />
      {loading ? (
        <SkeletonList />
      ) : visibleRoutines.length ? (
        <RoutineBoard routines={visibleRoutines} logs={logs} onCellClick={openModal} compact />
      ) : (
        <EmptyState icon="🧾" title="No routine found" text="Try another class, room, subject or teacher." />
      )}

      <Modal open={Boolean(modal)} title="Add Time from Routine" description="Tap save after checking IN and optional OUT time." onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSave}>
          <div className="selected-routine">
            <strong>{modal?.routine?.subjectName} · Room {modal?.roomNumber || modal?.routine?.roomNumber}</strong>
            <span>
              {modal?.routine?.className} - {modal?.routine?.sectionName} · {timeRangeLabel(modal?.routine?.scheduledIn, modal?.routine?.scheduledOut)}
            </span>
          </div>
          <div className="form-grid two">
            <Field label="Room number">
              <input value={modal?.roomNumber || ""} onChange={(event) => setModal((value) => ({ ...value, roomNumber: event.target.value }))} placeholder="Room number" />
            </Field>
            <Field label="Teacher">
              <select value={modal?.teacherName || ""} onChange={(event) => setModal((value) => ({ ...value, teacherName: event.target.value }))} required>
                <option value="">Select teacher</option>
                {(routineMasters?.teachers || []).map((teacher) => (
                  <option key={teacher.id} value={teacher.name}>{teacher.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="IN Time">
            <input type="time" value={modal?.actualIn || ""} onChange={(event) => setModal((value) => ({ ...value, actualIn: event.target.value }))} required />
          </Field>
          <label className="optional-out-toggle">
            <input type="checkbox" checked={Boolean(modal?.addOut)} onChange={(event) => setModal((value) => ({ ...value, addOut: event.target.checked, actualOut: value.actualOut || getNowTimeValue() }))} />
            <span>Optional: add OUT time now</span>
          </label>
          {modal?.addOut ? (
            <Field label="OUT Time">
              <input type="time" value={modal?.actualOut || ""} onChange={(event) => setModal((value) => ({ ...value, actualOut: event.target.value }))} required />
            </Field>
          ) : null}
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}><X size={17} />Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : <Save size={17} />}Save Time</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
